// src/app/core/interceptors/auth.interceptor.ts
import { HttpInterceptorFn, HttpRequest, HttpHandlerFn, HttpErrorResponse } from '@angular/common/http';
import { inject, isDevMode } from '@angular/core';
import { BehaviorSubject, from, throwError } from 'rxjs';
import { catchError, filter, switchMap, take, takeUntil, timeout } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';
import { HttpCancelService } from '../services/http-cancel.service';
import { environment } from '@env';

// Covers both /InsuranceAPI/api/... and /InsuranceAPI/GetProfile etc.
const API_BASE = environment.apiUrl.replace(/\/api$/, '');

// Only these endpoints may bypass auth headers (login, 2FA, email code, refresh, SSO redeem).
const SKIP_AUTH_WHITELIST = [
  '/Auth/InsuranceLogin',
  '/Auth/verify-2fa',
  '/Auth/send-email-code',
  '/Auth/RefreshToken',
  '/Auth/RedeemSsoTicket',
];

function isSkipAuthAllowed(url: string): boolean {
  return SKIP_AUTH_WHITELIST.some(path => url.includes(path));
}

/** Pre-auth endpoints where 401 means "invalid credentials", not "expired token" */
const isPreAuthUrl = (url: string) =>
  url.includes('/Auth/InsuranceLogin') ||
  url.includes('/Auth/verify-2fa') ||
  url.includes('/Auth/send-email-code');

// Shared state for queuing requests during token refresh
// null = in progress, true = refreshed, false = failed
let isRefreshing = false;
const refreshSubject = new BehaviorSubject<boolean | null>(null);

// Maximum time (ms) to wait for the refresh flow before giving up.
// This covers both the active refresh call and queued requests waiting
// for an in-flight refresh initiated by another request.
const REFRESH_TIMEOUT_MS = 15_000;

export const authInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
) => {
  const authService = inject(AuthService);
  const cancelService = inject(HttpCancelService);

  // X-Skip-Auth: only allowed for auth endpoints (login, 2FA, refresh, SSO)
  if (req.headers.has('X-Skip-Auth')) {
    if (isSkipAuthAllowed(req.url)) {
      const newReq = req.clone({
        headers: req.headers.delete('X-Skip-Auth')
      });
      return next(newReq);
    }
    if (isDevMode()) {
      console.warn('X-Skip-Auth rejected for non-whitelisted URL:', req.url);
    }
  }

  // Only add headers for API requests
  if (!req.url.startsWith(API_BASE)) {
    return next(req);
  }

  // Build headers
  let headers = req.headers.delete('X-Skip-Auth');

  if (!headers.has('API_KEY')) {
    headers = headers.set('API_KEY', environment.apiKey);
  }

  const token = authService.getToken();
  if (token) {
    headers = headers.set('Authorization', `Bearer ${token}`);
  }

  const clonedReq = req.clone({ headers });

  return next(clonedReq).pipe(
    takeUntil(cancelService.onCancelAll$),
    catchError((error: HttpErrorResponse) => {
      if (
        error.status === 401 &&
        !req.url.includes('/Auth/RefreshToken') &&
        !isPreAuthUrl(req.url)
      ) {
        return handle401(req, next, authService, cancelService);
      }
      return throwError(() => error);
    })
  );
};

function handle401(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  authService: AuthService,
  cancelService: HttpCancelService
) {
  if (!isRefreshing) {
    isRefreshing = true;
    refreshSubject.next(null);

    return from(authService.refreshAccessToken()).pipe(
      timeout(REFRESH_TIMEOUT_MS),
      switchMap(success => {
        isRefreshing = false;

        if (success) {
          refreshSubject.next(true);
          const newToken = authService.getToken();
          let headers = req.headers;
          if (newToken) {
            headers = headers.set('Authorization', `Bearer ${newToken}`);
          }
          if (!headers.has('API_KEY')) {
            headers = headers.set('API_KEY', environment.apiKey);
          }
          return next(req.clone({ headers })).pipe(
            takeUntil(cancelService.onCancelAll$)
          );
        }

        refreshSubject.next(false);
        authService.logout();
        return throwError(() => new HttpErrorResponse({ status: 401 }));
      }),
      catchError(err => {
        isRefreshing = false;
        refreshSubject.next(false);
        authService.logout();
        return throwError(() => err);
      })
    );
  }

  // Request came in while refreshing — wait for refresh to complete
  return refreshSubject.pipe(
    filter((v): v is boolean => v !== null),
    take(1),
    timeout(REFRESH_TIMEOUT_MS),
    switchMap(refreshed => {
      if (!refreshed) {
        return throwError(() => new HttpErrorResponse({ status: 401 }));
      }
      const newToken = authService.getToken();
      let headers = req.headers;
      if (newToken) {
        headers = headers.set('Authorization', `Bearer ${newToken}`);
      }
      if (!headers.has('API_KEY')) {
        headers = headers.set('API_KEY', environment.apiKey);
      }
      return next(req.clone({ headers })).pipe(
        takeUntil(cancelService.onCancelAll$)
      );
    }),
    catchError(err => {
      // Timeout or refresh failure — reject queued request
      isRefreshing = false;
      refreshSubject.next(false);
      return throwError(() => err);
    })
  );
}
